import {
	GalleryLightboxAds,
	GalleryLightboxAdsHandler,
	insertSlots,
	MessageBoxService,
	NativoSlotsDefinitionRepository,
	PlaceholderService,
	QuizSlotsDefinitionRepository,
	slotsContext,
} from '@platforms/shared';
import {
	AdSlotClass,
	AdSlotEvent,
	AdSlotStatus,
	Anyclip,
	btfBlockerService,
	communicationService,
	Connatix,
	context,
	CookieStorageAdapter,
	DiProcess,
	eventsRepository,
	globalContextService,
	InstantConfigService,
	Jwp,
	Nativo,
	slotImpactWatcher,
	slotService,
	UapLoadStatus,
	universalAdPackage,
} from '@wikia/ad-engine';
import { Injectable } from '@wikia/dependency-injection';
import {
	AD_ENGINE_INTERSTITIAL_DISPLAYED,
	AD_ENGINE_STACK_START,
} from '../../../../communication/events/events-ad-engine';
import { AD_ENGINE_SLOT_ADDED } from '../../../../communication/events/events-ad-engine-slot';
import { AD_ENGINE_UAP_LOAD_STATUS } from '../../../../communication/events/events-ad-engine-uap';
import {
	CONNATIX_LATE_INJECT,
	CONNATIX_READY,
} from '../../../../communication/events/events-connatix';
import { GAM_INTERSTITIAL_LOADED } from '../../../../communication/events/events-gam';
import { VIDEO_CLOSE_BUTTON_CLICKED } from '../../../../communication/events/events-video';
import { UcpMobileSlotsDefinitionRepository } from './ucp-mobile-slots-definition-repository';
import { JWP_ICV_INJECT, JWP_ICV_READY } from '../../../../communication/events/events-misc';
import {
	PLATFORM_MOBILE_DRAWER_POPULAR_VIDEO_CLOSED,
	PLATFORM_MOBILE_DRAWER_POPULAR_VIDEO_SELECTED,
	PLATFORM_MOBILE_DRAWER_STATE_CHANGE,
	PLATFORM_MOBILE_DRAWER_STATE_INITIAL,
} from '../../../../communication/events/events-platform-mobile-drawer';

@Injectable()
export class UcpMobileDynamicSlotsSetup implements DiProcess {
	constructor(
		private slotsDefinitionRepository: UcpMobileSlotsDefinitionRepository,
		private nativoSlotDefinitionRepository: NativoSlotsDefinitionRepository,
		private quizSlotsDefinitionRepository: QuizSlotsDefinitionRepository,
		private anyclip: Anyclip,
		private connatix: Connatix,
		private jwp: Jwp,
		private galleryLightbox: GalleryLightboxAds,
		protected instantConfig: InstantConfigService,
	) {}

	execute(): void {
		this.injectSlots();
		this.configureTopLeaderboardAndCompanions();
		this.configureMobileMoreIcbExperiment();
		this.configureIncontents();
		this.configureInterstitial();
		this.configureNativeAds();
		this.registerFloorAdhesionCodePriority();
		this.registerAdPlaceholderService();
		this.handleMobileGalleryLightboxAdsSlots();
	}

	private injectSlots(): void {
		const topLeaderboardDefinition = this.slotsDefinitionRepository.getTopLeaderboardConfig();

		insertSlots([
			topLeaderboardDefinition,
			this.slotsDefinitionRepository.getTopBoxadConfig(),
			this.slotsDefinitionRepository.getBottomLeaderboardConfig(),
			this.slotsDefinitionRepository.getMobilePrefooterConfig(),
			this.slotsDefinitionRepository.getInterstitialConfig(),
		]);

		if (context.get('custom.hasFeaturedVideo')) {
			this.waitForFloorAdhesionInjection();
		} else {
			insertSlots([this.slotsDefinitionRepository.getFloorAdhesionConfig()]);
		}

		if (!topLeaderboardDefinition) {
			communicationService.on(AD_ENGINE_STACK_START, () => {
				btfBlockerService.finishFirstCall();
				communicationService.emit(AD_ENGINE_UAP_LOAD_STATUS, {
					isLoaded: universalAdPackage.isFanTakeoverLoaded(),
					adProduct: universalAdPackage.getType(),
				});
			});
		}

		communicationService.on(
			eventsRepository.QUIZ_AD_INJECTED,
			(payload) => {
				insertSlots([this.quizSlotsDefinitionRepository.getQuizAdConfig(payload.slotId)]);
			},
			false,
		);
	}

	// Can remove after ADEN-14313 is done
	private configureMobileMoreIcbExperiment(): void {
		if (context.get('experiments.mobileMoreIcb')) {
			context.remove('slots.mobile_top_leaderboard');
			communicationService.emit(eventsRepository.MOBILE_MORE_ICB_EXPERIMENT);
		}
	}

	private configureTopLeaderboardAndCompanions(): void {
		if (
			!context.get('custom.hasFeaturedVideo') &&
			context.get('wiki.targeting.pageType') !== 'search'
		) {
			slotsContext.addSlotSize(
				'top_leaderboard',
				universalAdPackage.UAP_ADDITIONAL_SIZES.bfaSize.mobile,
			);
			slotsContext.addSlotSize(
				'top_leaderboard',
				universalAdPackage.UAP_ADDITIONAL_SIZES.bfaSize.unified,
			);

			context.push('slots.top_leaderboard.defaultTemplates', 'stickyTlb');
		}

		slotsContext.addSlotSize(
			'top_boxad',
			universalAdPackage.UAP_ADDITIONAL_SIZES.companionSizes['4x4'].size,
		);
		slotsContext.addSlotSize(
			'incontent_boxad_1',
			universalAdPackage.UAP_ADDITIONAL_SIZES.companionSizes['4x4'].size,
		);
		slotsContext.addSlotSize(
			'mobile_prefooter',
			universalAdPackage.UAP_ADDITIONAL_SIZES.companionSizes['4x4'].size,
		);
	}

	private configureNativeAds(): void {
		communicationService.on(AD_ENGINE_UAP_LOAD_STATUS, (action: UapLoadStatus) => {
			if (!action.isLoaded) {
				communicationService.on(eventsRepository.FAN_FEED_READY, () => {
					insertSlots([
						this.nativoSlotDefinitionRepository.getNativoFeedAdConfig({
							slotName: Nativo.FEED_AD_SLOT_NAME,
							anchorSelector:
								'.recirculation-prefooter > .recirculation-prefooter__recommendations',
							insertMethod: 'prepend',
							classList: ['ntv-ad', AdSlotClass.HIDDEN_AD_CLASS],
						}),
					]);
				});
			}
		});
	}

	private configureIncontents(): void {
		communicationService.on(AD_ENGINE_UAP_LOAD_STATUS, (action: UapLoadStatus) => {
			if (!action.isLoaded) {
				insertSlots([this.slotsDefinitionRepository.getIncontentPlayerConfig()]);
			}

			insertSlots([this.slotsDefinitionRepository.getIncontentBoxadConfig()]);
		});

		communicationService.on(
			PLATFORM_MOBILE_DRAWER_POPULAR_VIDEO_SELECTED,
			({ payload }) => {
				context.set('services.jwp.options', payload);
				insertSlots([this.slotsDefinitionRepository.getDrawerPlayerConfig()]);
			},
			false,
		);

		communicationService.on(
			PLATFORM_MOBILE_DRAWER_POPULAR_VIDEO_CLOSED,
			() => {
				const slotName = 'drawer';
				slotService.remove(slotService.get(slotName));
			},
			false,
		);

		communicationService.on(
			AD_ENGINE_SLOT_ADDED,
			({ slot }) => {
				if (slot.getSlotName() === 'incontent_player') {
					communicationService.emit(eventsRepository.ANYCLIP_LATE_INJECT);
					communicationService.emit(CONNATIX_LATE_INJECT);
					slot.getPlaceholder()?.classList.remove('is-loading');
				}
				if (slot.getSlotName() === 'drawer') {
					communicationService.emit(JWP_ICV_INJECT);
					slot.getPlaceholder()?.classList.remove('is-loading');
				}
			},
			false,
		);
	}

	private configureInterstitial(): void {
		const setInterstitialCapping = () => {
			const cookieAdapter = new CookieStorageAdapter();
			// setting TTL for this cookie to 24h
			cookieAdapter.setItem('_ae_intrsttl_imp', '1', 86400000);
		};

		communicationService.onSlotEvent(
			AdSlotEvent.SLOT_VIEWED_EVENT,
			() => {
				communicationService.emit(AD_ENGINE_INTERSTITIAL_DISPLAYED);
				setInterstitialCapping();
			},
			'interstitial',
		);

		communicationService.on(GAM_INTERSTITIAL_LOADED, () => {
			setInterstitialCapping();
		});
	}

	private waitForFloorAdhesionInjection(): void {
		communicationService.on(
			VIDEO_CLOSE_BUTTON_CLICKED,
			() => {
				insertSlots([this.slotsDefinitionRepository.getFloorAdhesionConfig()]);
			},
			true,
		);
	}

	private registerFloorAdhesionCodePriority(): void {
		const slotName = 'floor_adhesion';
		let codePriorityActive = false;
		let anyclipLoaded = false;
		let connatixLoaded = false;
		let jwpLoaded = false;
		let allowIcvFloat = false;

		const disableAnyclipFloating = () => {
			if (!anyclipLoaded) return;
			this.anyclip.disableFloating();
		};

		const disableConnatixFloating = () => {
			if (!connatixLoaded) return;
			this.connatix.disableFloating();
		};

		const disableJwpFloating = () => {
			if (!jwpLoaded) return;
			this.jwp.disableFloating();
		};

		const enableAnyclipFloating = () => {
			if (!anyclipLoaded) return;
			this.anyclip.enableFloating();
		};

		const enableConnatixFloating = () => {
			if (!connatixLoaded) return;
			this.connatix.enableFloating();
		};

		const hideFloorAdhesionAnchor = () => {
			document
				.getElementById('floor_adhesion_container')
				?.classList.add('hide', AdSlotClass.HIDDEN_AD_CLASS);
		};

		const disableFloorAdhesion = () => {
			const adSlot = slotService.get(slotName);
			if (adSlot) {
				slotService.remove(adSlot);
			}

			slotService.disable(slotName);
			slotImpactWatcher.disable([slotName]);
			hideFloorAdhesionAnchor();
		};

		slotImpactWatcher.request({
			id: slotName,
			priority: 4,
			breakCallback: disableFloorAdhesion,
		});

		if (context.get('experiments.floatingIcvWithDrawerExperiment.enabled')) {
			switch (context.get('experiments.floatingIcvWithDrawerExperiment.variant')) {
				case 1:
					communicationService.on(
						PLATFORM_MOBILE_DRAWER_STATE_INITIAL,
						({ state }) => {
							if (state === 'collapsed') {
								allowIcvFloat = true;
							}
						},
						false,
					);
					communicationService.on(
						PLATFORM_MOBILE_DRAWER_STATE_CHANGE,
						({ state }) => {
							if (state !== 'collapsed') {
								disableAnyclipFloating();
								disableConnatixFloating();
							}
						},
						false,
					);
					communicationService.onSlotEvent(AdSlotStatus.STATUS_SUCCESS, ({ adSlotName }) => {
						if (adSlotName === slotName) {
							allowIcvFloat = false;
						}
					});
					break;
				case 2:
					communicationService.on(
						PLATFORM_MOBILE_DRAWER_STATE_INITIAL,
						({ state }) => {
							if (state === 'collapsed') {
								allowIcvFloat = true;
								enableAnyclipFloating();
								enableConnatixFloating();
							}
						},
						false,
					);
					communicationService.on(
						PLATFORM_MOBILE_DRAWER_STATE_CHANGE,
						({ state }) => {
							if (state === 'collapsed') {
								enableAnyclipFloating();
								enableConnatixFloating();
							} else {
								disableAnyclipFloating();
								disableConnatixFloating();
							}
						},
						false,
					);
					break;
				case 3:
					communicationService.on(
						PLATFORM_MOBILE_DRAWER_STATE_INITIAL,
						() => {
							allowIcvFloat = true;
						},
						false,
					);
					break;
				default:
					break;
			}
		}

		communicationService.on(eventsRepository.ANYCLIP_READY, () => {
			anyclipLoaded = true;

			if (codePriorityActive) {
				disableAnyclipFloating();
			}
			if (allowIcvFloat) {
				enableAnyclipFloating();
			}
		});

		communicationService.on(CONNATIX_READY, () => {
			connatixLoaded = true;

			if (codePriorityActive) {
				disableConnatixFloating();
			}

			if (allowIcvFloat) {
				enableConnatixFloating();
			}
		});

		communicationService.on(JWP_ICV_READY, () => {
			jwpLoaded = true;

			if (codePriorityActive) {
				disableJwpFloating();
			}
		});

		communicationService.onSlotEvent(
			AdSlotStatus.STATUS_COLLAPSE,
			() => {
				slotImpactWatcher.disable([slotName]);
				hideFloorAdhesionAnchor();
			},
			slotName,
		);
		communicationService.onSlotEvent(
			AdSlotStatus.STATUS_FORCED_COLLAPSE,
			() => {
				slotImpactWatcher.disable([slotName]);
				hideFloorAdhesionAnchor();
			},
			slotName,
		);
		communicationService.onSlotEvent(
			AdSlotStatus.STATUS_SUCCESS,
			() => {
				if (typeof context.get('experiments.floatingIcvWithDrawerExperiment.variant') != 'number') {
					disableAnyclipFloating();
					disableConnatixFloating();
				}
				disableJwpFloating();

				communicationService.on(
					AD_ENGINE_INTERSTITIAL_DISPLAYED,
					() => {
						if (!codePriorityActive) {
							return;
						}

						slotImpactWatcher.request({
							id: 'interstitial',
							priority: 2,
						});
					},
					false,
				);

				communicationService.onSlotEvent(
					AdSlotEvent.CUSTOM_EVENT,
					({ payload }) => {
						if (payload.status !== AdSlotEvent.HIDDEN_EVENT) {
							return;
						}

						codePriorityActive = false;
						slotImpactWatcher.disable([slotName, 'interstitial']);
					},
					slotName,
				);
			},
			slotName,
		);
	}

	private registerAdPlaceholderService(): void {
		const messageBoxService = new MessageBoxService(context.get('services.messageBox.enabled'));
		const placeholderService = new PlaceholderService(messageBoxService);
		placeholderService.init();
	}

	private handleMobileGalleryLightboxAdsSlots(): void {
		const excludedBundleTagName = 'sensitive';
		const communityExcludedByTag = globalContextService.hasBundle(excludedBundleTagName);

		if (this.instantConfig.get('icMobileGalleryAds') && !communityExcludedByTag) {
			if (!this.galleryLightbox.initialized) {
				this.galleryLightbox.handler = new GalleryLightboxAdsHandler(
					this.slotsDefinitionRepository,
				);
				this.galleryLightbox.initialized = true;
			}
			this.galleryLightbox.handler.handle();
		}
	}
}
